import React from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import TopBar from '../components/TopBar';
import SafeIcon from '../common/SafeIcon';
import { moods, artists, tracks, systemMetrics } from '../data/mockData';

export default function Dashboard() {
  const radarOption = {
    radar: {
      indicator: [
        { name: 'Energy', max: 100 },
        { name: 'Acoustic', max: 100 },
        { name: 'Electronic', max: 100 },
        { name: 'Vocal', max: 100 },
        { name: 'Bass', max: 100 }
      ],
      shape: 'polygon',
      splitArea: { show: false },
      axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.2)' } },
      splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.1)' } },
      axisName: { color: '#a3a3a3' }
    },
    series: [{
      type: 'radar',
      data: [{
        value: [85, 20, 95, 40, 80],
        name: 'Profile',
        itemStyle: { color: '#00FFFF' },
        areaStyle: { color: 'rgba(0, 255, 255, 0.2)' },
        lineStyle: { width: 2, shadowBlur: 10, shadowColor: '#00FFFF' }
      }]
    }]
  };

  return (
    <div className="flex-1 overflow-y-auto w-full h-full bg-mesh-pattern pb-40">
      <TopBar title="Discovery Center" />
      
      <div className="p-6 max-w-7xl mx-auto space-y-8">
        
        {/* System Pulse Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {systemMetrics.map((metric, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: idx * 0.05 }}
              className="glass-panel p-4 rounded-xl border-l-2 border-l-aqua"
            >
              <p className="text-xs text-gray-400 uppercase tracking-wider">{metric.label}</p>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xl font-display font-bold text-white">{metric.value}</span>
                <SafeIcon 
                  icon={metric.trend === 'up' ? "FiTrendingUp" : "FiTrendingDown"} 
                  className={metric.trend === 'up' ? "text-green-400" : "text-plasma"} 
                />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mood Stations */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <SafeIcon icon="FiActivity" className="text-plasma" /> Active Mood Stations
            </h3>
            <button className="text-sm text-gray-400 hover:text-white">Expand Map</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            {moods.map((mood, idx) => (
              <motion.div
                key={mood.id}
                whileHover={{ y: -5 }}
                className="glass-panel p-4 rounded-xl relative overflow-hidden group cursor-pointer text-center"
              >
                <div className={`mx-auto mb-3 w-10 h-10 rounded-lg ${mood.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                  <SafeIcon icon={mood.icon} className="text-white text-xl" />
                </div>
                <h4 className="text-white text-xs font-medium truncate">{mood.name}</h4>
                <p className="text-[10px] text-gray-500 mt-1">{mood.frequency}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* Trending Shelf */}
            <section>
              <h3 className="text-lg font-semibold text-white mb-4">Neural Trending</h3>
              <div className="glass-panel rounded-2xl overflow-hidden divide-y divide-vinyl-700/30">
                {tracks.map((track, idx) => (
                  <div key={track.id} className="flex items-center gap-4 p-4 hover:bg-vinyl-700/50 transition-colors group cursor-pointer">
                    <span className="text-gray-600 font-display w-4">{idx + 1}</span>
                    <img src={track.cover} alt="" className="w-12 h-12 rounded-lg object-cover shadow-lg" />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white text-sm font-medium truncate">{track.title}</h4>
                      <p className="text-gray-400 text-xs truncate">{track.artist}</p>
                    </div>
                    <div className="hidden sm:block">
                      <span className={`text-[10px] px-2 py-1 rounded bg-vinyl-900 border ${track.status === 'Optimal' ? 'border-green-500/30 text-green-400' : 'border-plasma/30 text-plasma'}`}>
                        {track.status}
                      </span>
                    </div>
                    <span className="text-gray-500 text-xs font-mono">{track.duration}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* User Profile Metrics */}
          <div className="lg:col-span-1 space-y-6">
            <section className="glass-panel rounded-2xl p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10">
                <SafeIcon icon="FiActivity" className="text-6xl text-aqua" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Sonic Signature</h3>
              <p className="text-xs text-gray-400 mb-6">Real-time behavior analysis.</p>
              <div className="h-64">
                <ReactECharts option={radarOption} style={{ height: '100%' }} />
              </div>
            </section>

            <div className="glass-panel p-6 rounded-2xl border-t-2 border-t-plasma">
              <h4 className="text-sm font-bold text-white mb-4 uppercase tracking-widest">Recent Node Activity</h4>
              <div className="space-y-4">
                {[
                  { user: 'Alex_V', action: 'joined', target: 'Deep Space', time: '2m' },
                  { user: 'System', action: 'synced', target: 'Neon Rain', time: '12m' },
                  { user: 'Kai_Neon', action: 'broadcasted', target: 'Circuitry', time: '45m' },
                ].map((act, i) => (
                  <div key={i} className="flex gap-3 text-xs">
                    <div className="w-1 h-8 bg-vinyl-700 rounded-full" />
                    <div>
                      <p className="text-white">
                        <span className="text-aqua font-bold">{act.user}</span> {act.action} <span className="text-plasma">{act.target}</span>
                      </p>
                      <p className="text-gray-500 text-[10px]">{act.time} ago</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}